import { useEffect, useState } from "react";

/**
 * Today, for a tab that stays open.
 *
 * The board is full of "2d left" and "due this week", and all of it was read
 * off a date captured when the module first loaded. Leave the tab open
 * overnight and every one of those numbers is a day out - silently, because
 * nothing on screen says which day it was computed for.
 *
 * Two halves. `today()` is computed fresh on every call, so any render is
 * correct by construction; `useDayTick()` makes sure a render actually happens
 * when the day turns.
 */

export function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Re-renders the calling component when the date changes.
 *
 * Polled rather than scheduled for midnight: a timer set hours ahead is
 * throttled in a background tab and does not run at all while the machine is
 * asleep, which is exactly the case this exists for - a tab left open
 * overnight. A minute's granularity costs nothing and cannot be missed, and
 * waking the tab checks immediately rather than waiting for the next tick.
 */
export function useDayTick(): number {
  const [day, setDay] = useState(() => today().getTime());
  useEffect(() => {
    const check = () => setDay((prev) => {
      const now = today().getTime();
      return now === prev ? prev : now;   // same value ⇒ React skips the render
    });
    const id = window.setInterval(check, 60_000);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, []);
  return day;
}
